import { LocaleEnum, localeLabels } from '@systemeio/ui'
import React, { PropsWithChildren, memo } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { hexToRgbString } from 'shared/utils/hex-to-rgb-string'
import { CourseThemeColorSetInterface } from '../types/course-theme-color-set-interface'
import {
  ChevronDownIcon,
  CircleIcon,
  HomeIcon,
  PlayIcon,
  QuizIcon,
  RoundedCheckIcon,
} from '@systemeio/ui'

export interface CourseThemePreviewProps {
  theme?: CourseThemeColorSetInterface
  themeId: number
}

function HeaderMenuElement({ children, hasChevron }: PropsWithChildren & { hasChevron?: boolean }) {
  return (
    <span className="flex items-center gap-1 text-[4px] font-bold leading-[6px]">
      {children}
      {hasChevron && <ChevronDownIcon className="max-h-[4px] max-w-[4px]" />}
    </span>
  )
}

const stabCourseContent =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras suscipit ac mauris et eleifend. Suspendisse sed pretium est. Integer iaculis, risus ut vestibulum vehicula, velit est fringilla risus, nec eleifend nibh arcu a lorem. '

const progress = 30

function CourseThemePreview({ theme }: CourseThemePreviewProps) {
  const { t, i18n } = useLocoTranslation()

  if (!theme) return null

  return (
    <div
      className="bg-text-primary font-inter text-background-secondary h-48 w-full text-[4px] leading-[8px]"
      style={{ backgroundColor: theme.background }}
      role="img"
    >
      <div
        className="text-background-secondary flex h-5 w-full items-center justify-between px-2"
        style={{ backgroundColor: theme['header-background'] }}
      >
        <div className={'bg-sio_logo h-[10px] w-[10px] bg-contain'} />
        <div className="flex items-center gap-2">
          <HeaderMenuElement>
            <QuizIcon className="text-background-secondary max-h-[8px] max-w-[8px]" />
            {t('dashboard.courses.title_plural')}
          </HeaderMenuElement>
          <HeaderMenuElement>
            <HomeIcon className="fill-background-secondary max-h-[6px] max-w-[6px]" />
            {t('global.dashboard')}
          </HeaderMenuElement>
          <HeaderMenuElement hasChevron>
            <div className={'bg-user_stab_avatar h-[12px] w-[12px] bg-contain'} />
          </HeaderMenuElement>
          <HeaderMenuElement hasChevron>
            {i18n?.language && localeLabels[i18n.language as LocaleEnum]}
          </HeaderMenuElement>
        </div>
      </div>
      <div className="px-2.5 pt-[2px]">
        <span className="text-[5px] font-bold" style={{ color: theme.primary }}>
          {t('dashboard.course.theme.preview.course_name')}
        </span>
      </div>
      <div className="grid max-h-40 grid-cols-[5rem_1fr] grid-rows-[1fr_1.2rem_1fr] gap-2 overflow-hidden px-2 pt-[1px]">
        <div
          className="row-span-3 flex max-h-[9.4rem] flex-col gap-1 rounded-[4px] p-2"
          style={{ backgroundColor: theme['block-background'], color: theme.primary }}
        >
          <div className="flex w-full flex-col items-center justify-center gap-1">
            <div className={'bg-instructor_stab_avatar h-[20px] w-[20px] bg-contain'} />
            <span className="text-[5px] leading-[6px]">
              {t('dashboard.course.theme.preview.instructor_name')}
            </span>
          </div>
          <div className="flex items-center justify-evenly">
            <div
              className="rounded-xs flex min-w-[30px] justify-center border-[1px] py-0  leading-[7px]"
              style={{ borderColor: hexToRgbString(theme.primary, 0.25), color: theme.primary }}
            >
              {t('global.previous')}
            </div>
            <div
              className="text-background-secondary rounded-xs flex min-w-[30px] justify-center border-[1px] py-0 leading-[7px]"
              style={{ backgroundColor: theme.secondary, borderColor: theme.secondary }}
            >
              {t('global.next')}
            </div>
          </div>
          <div
            className={
              'relative flex max-h-[5px] w-full items-center justify-center overflow-hidden rounded-xl border-[1px] pt-[1px] text-center text-[3px] font-semibold'
            }
            style={{ color: theme.primary, borderColor: hexToRgbString(theme.primary, 0.25) }}
          >
            <div
              className={'absolute inset-0 h-full max-h-[5px] overflow-hidden rounded-xl'}
              style={{ width: `${progress}%`, backgroundColor: theme.primary }}
            >
              <div style={{ width: `${100 * (progress / 100)}%` }} />
            </div>
            {progress}%
          </div>
          <ul
            className="customScroll customScrollThin overflow-y-auto overflow-x-hidden pl-1 leading-[10px] tracking-tighter"
            onScroll={e => e.preventDefault()}
          >
            {Array.from({ length: 2 }, (_, i) => (
              <li key={i}>
                <span style={{ color: i === 0 ? theme.primary : theme.disabled }}>
                  {t('dashboard.course.theme.preview.module', { number: i + 1 })}
                </span>
                <ul className="pl-1">
                  {Array.from({ length: 3 }, (_, j) => (
                    <li
                      key={j}
                      className="flex items-center gap-[2px]"
                      style={{
                        color: i > 0 ? theme.disabled : j < 2 ? theme.primary : theme.secondary,
                      }}
                    >
                      {i === 0 && j < 2 ? (
                        <RoundedCheckIcon className="max-h-[4px] max-w-[4px]" />
                      ) : (
                        <CircleIcon className="max-h-[4px] max-w-[4px]" />
                      )}
                      {t('dashboard.course.theme.preview.lecture', { number: j + 1 })}
                    </li>
                  ))}
                  {i === 0 && (
                    <li className="flex items-center gap-[2px]" style={{ color: theme.primary }}>
                      <QuizIcon className="max-h-[6px] max-w-[6px]" />
                      {t('dashboard.course.theme.preview.quiz')}
                    </li>
                  )}
                </ul>
              </li>
            ))}
          </ul>
        </div>
        <div
          className="text-text-primary flex flex-col gap-3 rounded-[4px] p-2 text-[7px] leading-[7px]"
          style={{ backgroundColor: theme['block-background'] }}
        >
          <span className="font-bold">
            {t('dashboard.course.theme.preview.lecture', { number: 3 })}
          </span>
          <div className="from-text-tertiary relative h-[68px] w-[121px] self-center rounded-sm bg-gradient-to-b to-black">
            <PlayIcon className="text-background-tertiary absolute left-[50%] top-[50%] h-5 w-5 translate-x-[-50%] translate-y-[-50%]" />
          </div>
          <div className="text-[3px] leading-[4px]">{stabCourseContent}</div>
        </div>
        <div
          className="flex items-center justify-center rounded-[4px] p-2"
          style={{ backgroundColor: theme['block-background'] }}
        >
          <div
            className="rounded-xs flex min-w-[25px] justify-center border-[1px] px-1 py-0 leading-[7px]"
            style={{ borderColor: hexToRgbString(theme.primary, 0.25), color: theme.primary }}
          >
            {t('dashboard.course.theme.preview.action.complete')}
          </div>
        </div>
      </div>
    </div>
  )
}

export default memo(
  CourseThemePreview,
  (prevProps, nextProps) => prevProps.themeId === nextProps.themeId,
)
